import { useModals } from "@/features/modals/hooks";
import {
  type OpportunitiesSearchFilters,
  type OpportunitiesSearchOptions,
  generateFollowUpProjects,
} from "@repo/api";
import { Button, Option, Select, Text } from "@repo/skapa";
import { t } from "@repo/translation";
import {
  CoworkerAndTeamSelectorListItem,
  ModalSheet,
  SectionContainer,
  useToast,
} from "@repo/ui";
import { type ChangeEvent, useState } from "react";

interface GenerateProjectsModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: OpportunitiesSearchFilters;
  options: OpportunitiesSearchOptions;
}

export const GenerateProjectsModal = ({
  isOpen,
  onClose,
  filters,
  options,
}: GenerateProjectsModalProps) => {
  const { pushModal } = useModals();
  const { updateToast } = useToast();
  const [selectedCoWorker, setSelectedCoWorker] = useState<string | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(false);
  const [projectsCount, setProjectsCount] = useState<number>(10);
  const projectCountOptions = [5, 10, 15, 20];

  const onCloseModal = () => {
    setSelectedCoWorker(undefined);
    onClose();
  };

  const onGenerate = () => {
    setLoading(true);
    generateFollowUpProjects(selectedCoWorker!, projectsCount, filters, options)
      .then((res) => {
        updateToast({
          id: `generate-follow-up-projects-success-${res.requestId}`,
          title: t("prompt.generate_follow_up_list_success"),
        });
      })
      .catch((error) => {
        updateToast({
          id: `generate-follow-up-projects-failure`,
          title: "Failed to generate projects",
          message: error.message,
          type: "error",
        });
      })
      .finally(() => {
        setLoading(false);
        onCloseModal();
      });
  };

  return (
    <ModalSheet
      visible={isOpen}
      title={t("action.generate_follow_up_list")}
      onClose={onCloseModal}
      footer={
        <Button
          text={t("action.generate")}
          type="primary"
          disabled={!selectedCoWorker || !projectsCount}
          onClick={onGenerate}
          loading={loading}
        />
      }
    >
      <SectionContainer>
        <Text textStyle="Body.M">
          {t("prompt.generate_follow_up_list_description")}
        </Text>
        <CoworkerAndTeamSelectorListItem
          selectionMode={{ coworkerSelectionMode: "SINGLE" }}
          searchContext="CALENDAR"
          pushModal={pushModal}
          title={t("label.assigned_to")}
          selected={selectedCoWorker ? [selectedCoWorker] : []}
          setSelected={(s) => {
            const coworkers = Array.isArray(s) ? s : undefined;
            setSelectedCoWorker(coworkers?.[0]?.emailId);
          }}
        />
        <Select
          id="number-of-projects-select"
          label={t("label.number_of_projects")}
          value={String(projectsCount)}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
            setProjectsCount(Number(e.currentTarget.value));
          }}
        >
          {projectCountOptions?.map((option) => (
            <Option key={option} value={String(option)} name={String(option)} />
          ))}
        </Select>
      </SectionContainer>
    </ModalSheet>
  );
};
